import React, { useMemo } from 'react';
import { string } from 'prop-types';

import { ProductsTableLoader } from 'components/skeleton-loader';
import RCPagination from 'components/pagination/RCPagination';

import {
    FiltersTriggerButton,
    Search
} from 'components/FilteredListing/components';

import { useMobileFilters } from '../FilteredListing/hooks';

import { ActiveFilters, Filters, ProductsTable } from './components';
import { useProductFinderAPI } from './hooks';
import { URL_PARAMETER_PAGE } from '../FilteredListing/constants';
import { getLocalization } from '../FilteredListing/utils';

const ProductFinder = ({ configName }) => {
    const config = useMemo(() => {
        if (!window.config || !window.config[configName]) {
            return {};
        }

        return window.config[configName];
    }, []);

    const {
        api,
        culture,
        title,
        searchField,
        filtersTitle,
        filtersTitleIsHiddenOnMobile,
        listingTitle,
        numberFormat,
        siteName
    } = config;

    const {
        activeFilters,
        items,
        filters,
        pagination,
        paginationURLBase,
        nextItemsAmount,
        showPagination,
        searchValue,
        isLoading,
        isLoadingMore,
        scrollAnchorRef,
        handlePageChange,
        handleLoadMoreCLick,
        handleSearchInputChange,
        handleSubmit,
        handleCheckboxChange,
        handleChildCheckboxChange,
        handleClearFilters
    } = useProductFinderAPI({
        api,
        pagination: config.pagination,
        filters: config.filters,
        culture,
        siteName
    });
    const { toggleFilter } = useMobileFilters();

    return (
        <div className="c-listing">
            <div className="wrapper">
                <div className="c-listing__header">
                    <div className="grid">
                        <div className="col-md-4">
                            {title && (
                                <h1 className="c-listing__title h2">{title}</h1>
                            )}
                        </div>
                        {!!activeFilters && !!activeFilters.length && (
                            <div className="col-md-8">
                                <ActiveFilters
                                    activeFilters={activeFilters}
                                    clearFiltersButtonTitle={getLocalization(
                                        configName,
                                        'button/clearFilters'
                                    )}
                                    clearFilterTitle={getLocalization(
                                        configName,
                                        'filter/active/clear'
                                    )}
                                    handleClearFilters={handleClearFilters}
                                    onCheckboxChange={handleCheckboxChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="grid">
                    <div className="col-md-4">
                        <Filters
                            activeFilters={activeFilters}
                            clearFiltersButtonTitle={getLocalization(
                                configName,
                                'button/clearFilters'
                            )}
                            clearFilterTitle={getLocalization(
                                configName,
                                'filter/active/clear'
                            )}
                            closeButtonTitle={getLocalization(
                                configName,
                                'filter/modal/close'
                            )}
                            filters={filters}
                            filtersModalTitle={getLocalization(
                                configName,
                                'filter/modal/title'
                            )}
                            filtersTitle={filtersTitle}
                            filtersTitleIsHiddenOnMobile={
                                filtersTitleIsHiddenOnMobile
                            }
                            handleClearFilters={handleClearFilters}
                            isLoading={isLoading || isLoadingMore}
                            labelCollapse={getLocalization(
                                configName,
                                'filter/collapse'
                            )}
                            labelExpand={getLocalization(
                                configName,
                                'filter/expand'
                            )}
                            numberFormat={numberFormat}
                            results={pagination.rowsCount}
                            onCheckboxChange={handleCheckboxChange}
                            onChildCheckboxChange={handleChildCheckboxChange}
                            onCloseButtonClick={toggleFilter}
                            showResultsButtonTitle={getLocalization(
                                configName,
                                'button/showResults'
                            )}
                        />
                    </div>
                    <div className="col-md-8">
                        <div className="c-listing__search">
                            <Search
                                isLoading={isLoading || isLoadingMore}
                                searchButtonLabel={getLocalization(
                                    configName,
                                    'search/button'
                                )}
                                searchField={searchField}
                                searchValue={searchValue}
                                handleSubmit={handleSubmit}
                                onSearchInputChange={handleSearchInputChange}
                            />
                        </div>
                        <div className="c-listing__block c-listing__block--v-centered">
                            {listingTitle && (
                                <h2 className="h4">
                                    {listingTitle}{' '}
                                    <span className="results-amount">
                                        ({pagination.rowsCount})
                                    </span>
                                </h2>
                            )}
                        </div>
                        <div ref={scrollAnchorRef} />
                        {isLoading && (
                            <ProductsTableLoader
                                count={pagination.rowsOnPage}
                            />
                        )}
                        {!isLoading && !!items && !!items.length && (
                            <ProductsTable
                                items={items}
                                localization={{
                                    nameColTitle: getLocalization(
                                        configName,
                                        'products/table/column/name'
                                    ),
                                    groupColTitle: getLocalization(
                                        configName,
                                        'products/table/column/group'
                                    ),
                                    descriptionColTitle: getLocalization(
                                        configName,
                                        'products/table/column/description'
                                    ),
                                    detailButton: getLocalization(
                                        configName,
                                        'products/table/detailButton'
                                    ),
                                    showMore: getLocalization(
                                        configName,
                                        'products/table/showMore'
                                    ),
                                    showLess: getLocalization(
                                        configName,
                                        'products/table/showLess'
                                    )
                                }}
                            />
                        )}
                        {!isLoading && (!items || !items.length) && (
                            <p className="c-listing__no-results-text">
                                {getLocalization(
                                    configName,
                                    'listing/noResults'
                                )}
                            </p>
                        )}
                        {showPagination && (
                            <div className="c-listing__pagination">
                                <RCPagination
                                    current={pagination.currentPage}
                                    isLoading={isLoadingMore || isLoading}
                                    localization={{
                                        loadMore: getLocalization(
                                            configName,
                                            'pagination/loadMore'
                                        ),
                                        ariaLabel: getLocalization(
                                            configName,
                                            'pagination/ariaLabel'
                                        ),
                                        next: getLocalization(
                                            configName,
                                            'pagination/next'
                                        ),
                                        previous: getLocalization(
                                            configName,
                                            'pagination/previous'
                                        )
                                    }}
                                    nextItemsAmount={nextItemsAmount}
                                    onChange={handlePageChange}
                                    onLoadMoreCLick={handleLoadMoreCLick}
                                    pageSize={pagination.rowsOnPage}
                                    pageURLParameter={URL_PARAMETER_PAGE}
                                    total={pagination.rowsCount}
                                    urlBase={paginationURLBase}
                                    withLoadMore
                                />
                            </div>
                        )}
                    </div>
                </div>
                <FiltersTriggerButton
                    disabled={isLoading || isLoadingMore}
                    onClick={toggleFilter}
                    title={getLocalization(configName, 'filter/triggerButton')}
                />
            </div>
        </div>
    );
};

ProductFinder.propTypes = {
    configName: string.isRequired
};

export default ProductFinder;
